import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TOPIC_ICONS } from "@/lib/topics";
import {
  ArrowLeft, Mic, MicOff, ArrowUpRight, RotateCcw,
  ChevronRight, CheckCircle, XCircle, Volume2,
} from "lucide-react";
import AgentAvatar from "@/components/AgentAvatar";
import LiveAvatarVideo from "@/components/LiveAvatarVideo";
import LanguageToggle from "@/components/LanguageToggle";
import EventCodeGate from "@/components/EventCodeGate";
import InstructionModal from "@/components/InstructionModal";
import CinematicLoadingScreen from "@/components/CinematicLoadingScreen";

// Map topic id → agent key
const TOPIC_AGENT = {
  general_knowledge: "chanakya",
  know_your_bihar:   "ashoka",
  science:           "kalpana",
  hindi_cinema:      "bhawna",
};

// Local avatar paths (mirrors AgentAvatar.jsx LOCAL_AVATARS — note the typo "chankya")
const LOCAL_AVATAR_PATHS = {
  chanakya: "/chankya.png",
  ashoka:   "/ashoka.png",
  kalpana:  "/kalpana.png",
  bhawna:   "/bhawna.png",
  kiba:     "/veebee.png",
};

const BADGE_STYLE = {
  Expert:       { bg: "var(--kl-red)", color: "white" },
  Advanced:     { bg: "rgba(230,57,70,0.18)", color: "var(--kl-red)", border: "1px solid var(--kl-red)" },
  Intermediate: { bg: "rgba(255,255,255,0.06)", color: "white", border: "1px solid rgba(255,255,255,0.25)" },
  Beginner:     { bg: "transparent", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.18)" },
};

const OPTION_LETTERS = ["A", "B", "C", "D"];

/** Match spoken text to an option letter */
function matchVoiceToOption(transcript, options) {
  const t = transcript.toLowerCase().trim();

  // "option a" / "option b" / just "a" / "b" / "c" / "d" / "choice b" / "letter c"
  const letterMatch = t.match(/(?:option|letter|choice|select)?\s*\b([a-d])\b/);
  if (letterMatch) return letterMatch[1].toUpperCase();

  // Ordinal: "first", "second", "third", "fourth"
  const ordinals = { first: "A", second: "B", third: "C", fourth: "D" };
  for (const [word, letter] of Object.entries(ordinals)) {
    if (t.includes(word)) return letter;
  }

  // Match option text — compare cleaned keywords
  if (options) {
    for (let i = 0; i < options.length; i++) {
      const optText = options[i].replace(/^[A-D]\.\s*/i, "").toLowerCase();
      // take first 4 meaningful words as fingerprint
      const words = optText.split(/\s+/).filter((w) => w.length > 3).slice(0, 4).join(" ");
      if (words && t.includes(words)) return OPTION_LETTERS[i];
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Blitz() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // If coming from landing page modal, router state carries { player, verified: true }
  // Otherwise gate always shows fresh — no localStorage
  const [player, setPlayer] = useState(location.state?.player || null);
  const [eventVerified, setEventVerified] = useState(!!location.state?.verified);

  const [topics, setTopics]     = useState([]);
  const [agents, setAgents]     = useState({});
  const isChat = searchParams.get("mode") === "chat";
  const [showInstructions, setShowInstructions] = useState(true);
  // For chat: start at "mode" so user selects language before Kiba greets
  const [stage, setStage]       = useState("mode"); // mode | language | chat_ready | pre_chat | topic | loading | quiz | result | chat
  const [topic, setTopic]       = useState(null);
  const [lang, setLang]         = useState("en");
  const [agentGender, setAgentGender] = useState("male"); // "male" | "female"
  const [liveMode, setLiveMode] = useState(null); // true = Live Avatar (EN only) | false = AI Voice (EN/HI/BHO)

  // MCQ quiz state
  const [session, setSession]   = useState(null);      // {session_id, questions, topic_label, ...}
  const [qIdx, setQIdx]         = useState(0);         // current question index
  const [answers, setAnswers]   = useState([]);        // array of submitted option letters
  const [selected, setSelected] = useState("");        // current selection (before submit)
  const [subStage, setSubStage] = useState("speaking"); // speaking | answering | feedback
  const [timer, setTimer]       = useState(30);
  const [result, setResult]     = useState(null);
  const [scoreReveal, setScoreReveal] = useState(0);
  const [voiceActive, setVoiceActive] = useState(false);
  const timerRef  = useRef(null);

  // Blitz attempt tracking
  const [myAttempts, setMyAttempts] = useState(null); // { attempts_used, can_play, topics_played, sessions }
  const recogRef  = useRef(null);

  // Chat mode state
  const [chatHistory, setChatHistory]   = useState([]);
  const [chatInput, setChatInput]       = useState("");
  const [chatLoading, setChatLoading]   = useState(false);
  const [chatListening, setChatListening] = useState(false);
  const chatRecogRef = useRef(null);
  const chatEndRef   = useRef(null);

  useEffect(() => {
    if (!player) { toast.error("Please register first"); navigate("/"); return; }
    api.topics().then((r) => setTopics(r.topics)).catch(() => {});
    api.agents().then((r) => setAgents(r.agents || {})).catch(() => {});
  }, [navigate, player]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  // ── Score reveal animation ──────────────────────────────────────────────
  useEffect(() => {
    if (stage !== "result" || !result) return;
    let s = 0;
    const target = result.score;
    const id = setInterval(() => { s += 1; setScoreReveal(s); if (s >= target) clearInterval(id); }, 90);
    return () => clearInterval(id);
  }, [stage, result]);

  // ── Per-question timer (answering subStage only) ────────────────────────
  useEffect(() => {
    if (stage !== "quiz" || subStage !== "answering") return;
    setTimer(30);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) { clearInterval(timerRef.current); submitAnswer(""); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, subStage, qIdx]);

  // ── Voice for MCQ options ────────────────────────────────────────────────
  const startVoiceListen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Speech recognition not supported in this browser"); return; }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = (lang === "hi" || lang === "bho") ? "hi-IN" : "en-IN";
    rec.onresult = (e) => {
      const transcript = fixTranscript(e.results[0]?.[0]?.transcript || "");
      const currentOptions = session?.questions[qIdx]?.options;
      const matched = matchVoiceToOption(transcript, currentOptions);
      if (matched) {
        setSelected(matched);
        toast.success(`Heard: Option ${matched}`);
      } else {
        toast("Couldn't match option — try saying 'Option A', 'B', or reading the option.");
      }
    };
    rec.onerror  = () => setVoiceActive(false);
    rec.onend    = () => setVoiceActive(false);
    rec.start();
    recogRef.current = rec;
    setVoiceActive(true);
  };

  const stopVoiceListen = () => {
    if (recogRef.current) try { recogRef.current.stop(); } catch {}
    setVoiceActive(false);
  };

  // ── Load attempt history when reaching topic stage ───────────────────────
  useEffect(() => {
    if (stage === "topic" && player?.phone) {
      api.blitzMyAttempts(player.phone)
        .then((d) => setMyAttempts(d))
        .catch(() => {}); // silently ignore — non-critical
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // ── Pick topic & load MCQ questions ──────────────────────────────────────
  const pickTopic = async (t) => {
    setTopic(t);
    setStage("loading");
    try {
      const r = await api.blitzMcqStart({ ...player, topic: t.id, lang, agent_gender: agentGender });
      setSession(r);
      setQIdx(0);
      setAnswers([]);
      setSelected("");
      setSubStage("speaking");
      setStage("quiz");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start quiz");
      setStage("topic");
    }
  };

  // ── Submit answer for current question ───────────────────────────────────
  const submitAnswer = useCallback((opt) => {
    clearInterval(timerRef.current);
    stopVoiceListen();
    const answer = opt !== undefined ? opt : selected;
    setAnswers((prev) => {
      const next = [...prev];
      next[qIdx] = answer;
      return next;
    });
    setSubStage("feedback");
  }, [qIdx, selected]);

  // ── Move to next question or finish ───────────────────────────────────────
  const advanceQuestion = useCallback(async () => {
    const nextIdx = qIdx + 1;
    if (nextIdx >= (session?.questions?.length || 10)) {
      // Quiz complete — submit to backend
      const finalAnswers = [...answers];
      // Patch in any unanswered questions as ""
      while (finalAnswers.length < session.questions.length) finalAnswers.push("");
      setStage("eval");
      try {
        const r = await api.blitzMcqComplete({
          session_id: session.session_id,
          answers: finalAnswers,
        });
        setResult(r);
        setScoreReveal(0);
        setStage("result");
      } catch (e) {
        toast.error("Failed to save results");
        setStage("quiz");
      }
    } else {
      setQIdx(nextIdx);
      setSelected("");
      setSubStage("speaking");
    }
  }, [qIdx, session, answers]);

  // ── Chat: send message ────────────────────────────────────────────────────
  const sendChat = async (text) => {
    const msg = (text || chatInput).trim();
    if (!msg) return;
    setChatInput("");
    const userMsg = { role: "user", content: msg };
    setChatHistory((h) => [...h, userMsg]);
    setChatLoading(true);
    try {
      const r = await api.chatMessage({
        player_name: player?.name || "",
        message: msg,
        history: chatHistory.slice(-8),
        lang,
      });
      setChatHistory((h) => [...h, { role: "assistant", content: r.response }]);
    } catch {
      toast.error("Couldn't reach NOVA. Try again.");
    } finally {
      setChatLoading(false);
    }
  };

  // Fix common speech-to-text misrecognitions of brand names
  const fixTranscript = (text) => {
    const fixes = [
      // "Kiba Labs" variants
      [/\bcable\s+labs?\b/gi,  "Kiba Labs"],
      [/\bcable\s+apps?\b/gi,  "Kiba Labs"],
      [/\bkeeba\s+labs?\b/gi,  "Kiba Labs"],
      [/\bkeba\s+labs?\b/gi,   "Kiba Labs"],
      [/\bkiva\s+labs?\b/gi,   "Kiba Labs"],
      [/\bkibo\s+labs?\b/gi,   "Kiba Labs"],
      [/\bmy\s+apps?\b/gi,     "Kiba Labs"],
      [/\bkiba\s+lab\b/gi,     "Kiba Labs"],
      // "Kiba" standalone
      [/\bkeeba\b/gi,  "Kiba"],
      [/\bkeba\b/gi,   "Kiba"],
      [/\bkiva\b/gi,   "Kiba"],
      [/\bkibo\b/gi,   "Kiba"],
      [/\bkeba\b/gi,   "Kiba"],
    ];
    return fixes.reduce((t, [pattern, replacement]) => t.replace(pattern, replacement), text);
  };

  const toggleChatMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Speech recognition not supported"); return; }
    if (chatListening) {
      if (chatRecogRef.current) try { chatRecogRef.current.stop(); } catch {}
      setChatListening(false);
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = (lang === "hi" || lang === "bho") ? "hi-IN" : "en-IN";
    rec.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript + " ";
      setChatInput(fixTranscript(txt.trim()));
    };
    rec.onend  = () => setChatListening(false);
    rec.onerror = () => setChatListening(false);
    rec.start();
    chatRecogRef.current = rec;
    setChatListening(true);
  };
  

  // Greeting script per language
  const getGreeting = (language) => {
    const firstName = (player?.name || "Friend").split(" ")[0];
    const scripts = {
      en:  `Hi ${firstName}! I am Kiba, your AI Guide at the Proctorly booth, brought to you by Kiba Labs. I can tell you everything about Proctorly  what it does, how it works, pricing, how to get a demo. But I am not just about Proctorly. Want to talk careers and where the industry is heading? Let us do it. Curious about AI and the future of technology? I am all in. Want to debate IPL teams, discuss Bihar's incredible history, or get into global geopolitics? I am ready for all of it. No topic is off limits and no question is too small. So tell me  what is on your mind today?`,
      hi:  `नमस्ते ${firstName}! मैं Kiba हूँ  Kiba Labs की AI Summit में Proctorly booth की आपकी AI Guide। Proctorly क्या है, कैसे काम करता है, pricing, demo  सब बता सकती हूँ। और सिर्फ Proctorly ही नहीं  करियर, टेक्नोलॉजी, AI का भविष्य, बिहार की गौरवशाली विरासत, IPL और क्रिकेट, देश-दुनिया की जियोपॉलिटिक्स  हर टॉपिक पर बात हो सकती है। मैं आपकी सबसे स्मार्ट दोस्त हूँ  और आज पूरी तरह आपके लिए हूँ। बताइए  कहाँ से शुरू करें ?`,
      bho: `प्रणाम ${firstName}!! हम Kiba हईं, Kiba Labs के Proctorly booth के आपके AI Guide। Proctorly का बा, कइसे काम करेला, pricing का बा, demo कइसे लीं सब कुछ बता सकत हईं। आ बस Proctorly ही ना करियर के राह, टेक्नोलॉजी के दुनिया, AI के भविष्य, बिहार के गौरव आ इतिहास, IPL आ क्रिकेट के मजा, आ दुनिया भर के जियोपॉलिटिक्स — हर बात पर बतिया सकत हईं। हम आपके सबसे होशियार दोस्त हईं आ आज पूरी तरह आपके साथ हईं। बताईं का जाने के मन बा?`,
    };
    return scripts[language] || scripts.en;
  };

  // When language is toggled while inside chat, re-issue the greeting in the new language
  useEffect(() => {
    if (stage !== "chat" || !player) return;
    setChatHistory([{ role: "assistant", content: getGreeting(lang) }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, stage, player]);

  // Auto-advance from chat_loading → chat_ready after 3 s (time for Live Avatar to warm up)
  useEffect(() => {
    if (stage !== "chat_loading") return;
    const id = setTimeout(() => setStage("chat_ready"), 3000);
    return () => clearTimeout(id);
  }, [stage]);

  // Safety net: if instructions were already dismissed and we hit chat_ready, go straight to chat
  useEffect(() => {
    if (stage !== "chat_ready") return;
    if (!showInstructions) startChat();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Init chat with greeting — called after instructions are dismissed
  const startChat = () => {
    setChatHistory([{ role: "assistant", content: getGreeting(lang) }]);
    setStage("chat");
  };


  if (!eventVerified) {
    return (
      <EventCodeGate
        onVerified={(u) => {
          if (u) setPlayer(u);
          setEventVerified(true);
        }}
      />
    );
  }

  const agentKey  = topic ? (TOPIC_AGENT[topic.id] || "chanakya") : "chanakya";
  const agentInfo = agents[agentKey] || {};

  // Combo name for loading screen
  const TOPIC_GROUP_MAP_MAIN = { science: "A", general_knowledge: "A", know_your_bihar: "B", hindi_cinema: "B" };
  const COMBO_NAME_MAIN = {
    "A-male-en": "John",   "A-male-hi": "James",   "A-male-bho": "Lallan",
    "A-female-en": "Emma", "A-female-hi": "Nimmi",  "A-female-bho": "Gudiya",
    "B-male-en": "Peter",  "B-male-hi": "Russell",  "B-male-bho": "Bablu",
    "B-female-en": "Lilly","B-female-hi": "Bella",  "B-female-bho": "Champa",
  };
  const loadingGroup = topic ? (TOPIC_GROUP_MAP_MAIN[topic.id] || "A") : "A";
  const loadingComboName = COMBO_NAME_MAIN[`${loadingGroup}-${agentGender}-${lang}`] || agentInfo.name || "AI Agent";

  return (
    <div className="min-h-screen text-white" style={{ background: "var(--kl-navy-deep)" }}>
      {/* ── Instruction modal — shown once before blitz/chat starts ── */}
      {showInstructions && stage === "chat_ready" && (
        <InstructionModal
          mode="chat"
          initialLang={lang}
          hostAvatar="/kiba.png"
                                                                          onStart={() => {
            setShowInstructions(false);
            startChat();
          }}
        />
      )}
      {/* Blitz quiz instruction — shown when user first reaches the topic picker */}
      {showInstructions && !isChat && stage === "topic" && (
        <InstructionModal
          mode="blitz"
          initialLang={lang}
          onStart={() => setShowInstructions(false)}
        />
      )}

      <Header />

      {/* ── QUIZ: full-viewport, no container constraint ─────────────────── */}
      {stage === "quiz" && session && (
        <QuizQuestion
          session={session}
          qIdx={qIdx}
          answers={answers}
          selected={selected}
          setSelected={setSelected}
          subStage={subStage}
          setSubStage={setSubStage}
          timer={timer}
          lang={lang}
          setLang={setLang}
          agentGender={agentGender}
          agentKey={agentKey}
          agentInfo={agentInfo}
          useLiveAvatar={liveMode === true}
          voiceActive={voiceActive}
          startVoiceListen={startVoiceListen}
          stopVoiceListen={stopVoiceListen}
          onSubmit={submitAnswer}
          onAdvance={advanceQuestion}
          onBack={() => { setSession(null); setStage("mode"); }}
          player={player}
        />
      )}

      <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-10 relative">
        <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(230,57,70,0.6), transparent 60%)" }} />

        {stage !== "quiz" && stage !== "chat" && (
          <>
            <button onClick={() => navigate("/")} className="relative z-10 font-mono-kl text-xs uppercase tracking-widest flex items-center gap-2 mb-6 text-white/60 hover:text-white">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </button>

            <div className="relative z-10 font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">
              <span className="kl-accent-line mr-3" />
              BLITZ · {player.name.toUpperCase()}
            </div>
          </>
        )}

        {/* ── MODE PICKER ───────────────────────────────────────────────────── */}
        {stage === "mode" && (
          <div className="mt-12 relative z-10 slide-up flex flex-col items-center text-center">
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-4">
              Step 1 of 3 · Choose your experience
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight text-white">
              How do you<br /><span style={{ color: "var(--kl-red)" }}>want it?</span>
            </h1>
            <p className="mt-5 text-white/50 font-mono-kl text-sm max-w-sm">
              Pick the type of AI agent that will host your quiz.
            </p>

            <div className="mt-12 w-full max-w-2xl flex flex-col sm:flex-row gap-6">
              {/* ── Live Avatar card ── */}
              <button
                onClick={() => { setLiveMode(true); setLang("en"); setStage("language"); }}
                className="flex-1 flex flex-col items-center gap-4 px-8 py-10 rounded-2xl border transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: "rgba(230,57,70,0.07)", border: "2px solid rgba(230,57,70,0.45)", color: "#fff" }}
              >
                <div style={{ fontSize: 48 }}>🎥</div>
                <div className="font-display text-3xl uppercase tracking-tight">Live AI Avatar</div>
                <div className="font-mono-kl text-xs text-white/60 leading-relaxed text-center">
                  Real-time lip-sync video<br />{isChat ? "KIBA speaks & listens" : "Avatar speaks every question"}
                </div>
                <div className="mt-2 px-4 py-1.5 rounded-full font-mono-kl text-[10px] uppercase tracking-widest"
                  style={{ background: "rgba(230,57,70,0.2)", color: "var(--kl-red)", border: "1px solid rgba(230,57,70,0.4)" }}>
                  English only
                </div>
              </button>

              {/* ── AI Voice card ── */}
              <button
                onClick={() => { setLiveMode(false); setStage("language"); }}
                className="flex-1 flex flex-col items-center gap-4 px-8 py-10 rounded-2xl border transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: "rgba(255,255,255,0.03)", border: "2px solid rgba(255,255,255,0.12)", color: "#fff" }}
              >
                <div style={{ fontSize: 48 }}>🎙️</div>
                <div className="font-display text-3xl uppercase tracking-tight">AI Voice</div>
                <div className="font-mono-kl text-xs text-white/60 leading-relaxed text-center">
                  High-quality voice · Static avatar<br />Full language support
                </div>
                <div className="mt-2 px-4 py-1.5 rounded-full font-mono-kl text-[10px] uppercase tracking-widest"
                  style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.15)" }}>
                  English · Hindi · Bhojpuri
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ── LANGUAGE PICKER ───────────────────────────────────────────────── */}
        {stage === "language" && (
          <div className="mt-12 relative z-10 slide-up flex flex-col items-center text-center">
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-4">
              Step 2 of {isChat ? "2" : "3"} · {isChat ? "Choose Language" : "Choose Your AI Agent"}
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight text-white">
              {isChat ? <>Pick your<br /><span style={{ color: "var(--kl-red)" }}>language.</span></> : <>Pick your<br /><span style={{ color: "var(--kl-red)" }}>agent.</span></>}
            </h1>
            <p className="mt-5 text-white/50 font-mono-kl text-sm max-w-sm">
              {liveMode
                ? (isChat ? "Live Avatar requires English. KIBA will respond in English." : "Live Avatar requires English. Choose your agent voice.")
                : (isChat ? "Choose the language for your chat with KIBA." : "Choose the language and the agent voice for your quiz.")}
            </p>

            {/* ── Language (AI Voice only — Live Avatar is English-locked) ── */}
            {liveMode ? (
              <div className="mt-10 w-full max-w-lg">
                <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3 text-left">Language</div>
                <div className="flex items-center gap-3 px-6 py-4 rounded-xl"
                  style={{ background: "rgba(230,57,70,0.08)", border: "2px solid rgba(230,57,70,0.35)" }}>
                  <span style={{ fontSize: 20 }}>🎥</span>
                  <div className="text-left">
                    <div className="font-display text-xl uppercase text-white">English</div>
                    <div className="font-mono-kl text-[10px] text-white/50 uppercase tracking-widest">Live Avatar · English only</div>
                  </div>
                  <div className="ml-auto font-mono-kl text-[10px] uppercase tracking-widest px-3 py-1 rounded-full"
                    style={{ background: "rgba(230,57,70,0.2)", color: "var(--kl-red)" }}>Locked</div>
                </div>
              </div>
            ) : (
              <div className="mt-10 w-full max-w-lg">
                <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3 text-left">Language</div>
                <div className="flex gap-3">
                  {[
                    { code: "en",  label: "English",  sub: "Questions & voice in English" },
                    { code: "hi",  label: "हिन्दी",    sub: "प्रश्न और आवाज़ हिन्दी में" },
                    { code: "bho", label: "भोजपुरी",  sub: "सवाल अउर आवाज भोजपुरी में" },
                  ].map(({ code, label, sub }) => (
                    <button
                      key={code}
                      onClick={() => setLang(code)}
                      className="flex-1 flex flex-col items-center gap-2 px-4 py-6 rounded-xl border transition-all"
                      style={lang === code
                        ? { background: "rgba(230,57,70,0.12)", border: "2px solid var(--kl-red)", color: "#fff" }
                        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                      }
                    >
                      <span className="font-display text-2xl uppercase">{label}</span>
                      <span className="font-mono-kl text-[10px] uppercase tracking-widest opacity-60 leading-snug text-center">{sub}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Agent gender — quiz only ── */}
            {!isChat && (
              <div className="mt-8 w-full max-w-lg">
                <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3 text-left">AI Agent Voice</div>
                <div className="flex gap-4">
                  {[
                    { code: "male",   label: "Male Agent",   icon: "♂", sub: "Male AI voice" },
                    { code: "female", label: "Female Agent", icon: "♀", sub: "Female AI voice" },
                  ].map(({ code, label, icon, sub }) => (
                    <button
                      key={code}
                      onClick={() => setAgentGender(code)}
                      className="flex-1 flex flex-col items-center gap-2 px-6 py-6 rounded-xl border transition-all"
                      style={agentGender === code
                        ? { background: "rgba(230,57,70,0.12)", border: "2px solid var(--kl-red)", color: "#fff" }
                        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                      }
                    >
                      <span className="text-3xl">{icon}</span>
                      <span className="font-display text-xl uppercase">{label}</span>
                      <span className="font-mono-kl text-[10px] uppercase tracking-widest opacity-60">{sub}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Buttons ── */}
            <div className="mt-10 flex items-center gap-4">
              <button
                onClick={() => { setStage("mode"); setShowInstructions(true); }}
                className="px-8 py-4 rounded-xl font-display text-lg uppercase tracking-wide transition-all"
                style={{ background: "transparent", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.15)" }}
              >
                ← Back
              </button>
              <button
                onClick={() => isChat ? setStage("chat_loading") : setStage("topic")}
                className="px-12 py-4 rounded-xl font-display text-xl uppercase tracking-wide transition-all"
                style={{ background: "var(--kl-red)", color: "#fff", border: "2px solid var(--kl-red)" }}
              >
                {isChat ? "Chat with KIBA →" : "Continue → Pick Topic"}
              </button>
            </div>
          </div>
        )}

        {/* ── TOPIC PICKER ──────────────────────────────────────────────────── */}
        {stage === "topic" && (
          <div className="mt-5 relative z-10 slide-up">
            <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight text-white">
              Pick your<br /><span className="text-[color:var(--kl-red)]">battleground.</span>
            </h1>
            <p className="mt-4 text-white/60 max-w-2xl">Choose a topic. 10 MCQ questions, 30 seconds each. &nbsp;
              <span style={{ color: "var(--kl-red)" }}>{lang === "hi" ? "हिन्दी" : lang === "bho" ? "भोजपुरी" : "English"}</span>
              {" · "}
              <span style={{ color: "var(--kl-red)" }}>{agentGender === "female" ? "Female Agent" : "Male Agent"}</span>
              {" · "}
              <span style={{ color: "var(--kl-red)" }}>{liveMode ? "🎥 Live Avatar" : "🎙️ AI Voice"}</span>
              <button onClick={() => setStage("language")} className="ml-3 underline text-white/40 hover:text-white text-xs font-mono-kl uppercase tracking-widest">Change</button>
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-9">
              {/* All-plays-used banner */}
              {myAttempts && !myAttempts.can_play && (
                <div className="col-span-2 lg:col-span-4 rounded-xl border border-[color:var(--kl-red)]/40 p-5"
                  style={{ background: "rgba(230,57,70,0.08)" }}>
                  <div className="font-display text-xl uppercase text-[color:var(--kl-red)] mb-1">
                    You've used both Blitz attempts!
                  </div>
                  <p className="text-white/60 text-sm">
                    Each visitor can play Blitz in 2 different topic areas. You've completed your 2 sessions at this event.
                    Head to the <strong className="text-white">Proctorly booth</strong> to claim your prize if you scored 80%+!
                  </p>
                </div>
              )}
              {topics.map((t) => {
                const Icon = TOPIC_ICONS[t.icon] || TOPIC_ICONS.globe;
                const alreadyPlayed = myAttempts?.topics_played?.includes(t.id);
                const noPlaysLeft   = myAttempts && !myAttempts.can_play;
                const disabled      = alreadyPlayed || noPlaysLeft;
                return (
                  <button
                    key={t.id}
                    onClick={() => !disabled && pickTopic(t)}
                    disabled={disabled}
                    className="group text-left flex flex-col relative"
                    style={{
                      minHeight: 380,
                      padding: "2.25rem",
                      borderRadius: 20,
                      background: "#000",
                      border: `2px solid ${disabled ? "rgba(255,255,255,0.06)" : "rgba(230,57,70,0.6)"}`,
                      boxShadow: disabled ? "none" : "0 0 40px rgba(230,57,70,0.4), 0 0 80px rgba(230,57,70,0.15)",
                      opacity: disabled ? 0.5 : 1,
                      cursor: disabled ? "not-allowed" : "pointer",
                      transition: "box-shadow 0.3s, border-color 0.3s",
                      overflow: "hidden",
                    }}
                  >
                    {/* Radial glow backdrop */}
                    {!disabled && (
                      <div style={{
                        position: "absolute", inset: 0, pointerEvents: "none",
                        background: "radial-gradient(ellipse at 50% 100%, rgba(230,57,70,0.35) 0%, transparent 65%)",
                        borderRadius: 20,
                      }} />
                    )}
                    {alreadyPlayed && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono-kl uppercase tracking-widest"
                        style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}>
                        ✓ Played
                      </div>
                    )}
                    <Icon className={`w-12 h-12 mb-6 relative z-10 ${disabled ? "text-white/25" : "text-[color:var(--kl-red)]"}`} />
                    <div className="font-display text-3xl uppercase leading-tight text-white relative z-10">{t.label}</div>
                    <div className="text-base text-white/55 mt-4 flex-1 leading-relaxed relative z-10">{t.description}</div>
                    {!disabled && (
                      <div className="flex items-center gap-1.5 text-sm font-mono-kl uppercase tracking-widest text-[color:var(--kl-red)] mt-8 group-hover:translate-x-1 transition-transform relative z-10">
                        Start <ArrowUpRight className="w-4 h-4" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── LOADING ───────────────────────────────────────────────────────── */}
        {stage === "loading" && (() => {
          const COMBO_AVATAR_MAIN = {
            "A-male-en": "/male-1.png", "A-male-hi": "/male-2.png", "A-male-bho": "/33-boy.png",
            "A-female-en": "/fe-1.png",  "A-female-hi": "/fe-3.png",  "A-female-bho": "/fe-4.png",
            "B-male-en": "/male-3.png",  "B-male-hi": "/male-4.png",  "B-male-bho": "/33-boy.png",
            "B-female-en": "/fe-6.svg",  "B-female-hi": "/fe-2.png",  "B-female-bho": "/fe-3.png",
          };
          const loadingKey  = `${loadingGroup}-${agentGender}-${lang}`;
          const loadingAvatarSrc = COMBO_AVATAR_MAIN[loadingKey] || "/fe-1.png";
          const langLabel = lang === "hi" ? "हिन्दी" : lang === "bho" ? "भोजपुरी" : "ENGLISH";
          return (
            <CinematicLoadingScreen
              avatarSrc={loadingAvatarSrc}
              agentName={loadingComboName}
              statusLabel="GENERATING CHALLENGE"
              headline={<>{loadingComboName}<br /><span style={{ color: "var(--kl-red)" }}>is crafting</span><br />your quiz.</>}
              metaLine={`${topic?.label?.toUpperCase() ?? "QUIZ"} · ${langLabel} · 10 QUESTIONS`}
              progressDuration={6}
            />
          );
        })()}

        {/* ── QUIZ: rendered outside this container (full-viewport) ──────── */}


        {/* ── EVAL ─────────────────────────────────────────────────────────── */}
        {stage === "eval" && (
          <div className="text-center py-32 relative z-10 slide-up">
            <div className="font-display text-6xl uppercase text-white">Scoring your quiz…</div>
            <div className="mt-4 font-mono-kl uppercase tracking-widest text-sm text-white/50">Proctorly AI · Calculating results</div>
            <div className="flex justify-center gap-2 mt-8">
              {[0, 1, 2].map((i) => (
                <div key={i} className="w-3 h-3 rounded-full bg-[color:var(--kl-red)]" style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}

        {/* ── RESULT ───────────────────────────────────────────────────────── */}
        {stage === "result" && result && (
          <Result
            result={result}
            player={player}
            session={session}
            scoreReveal={scoreReveal}
            onReplay={() => { setResult(null); setSession(null); setShowInstructions(true); setStage("language"); }}
            navigate={navigate}
          />
        )}

        {/* ── CHAT LOADING ─────────────────────────────────────────────────── */}
        {stage === "chat_loading" && (
          <CinematicLoadingScreen
            avatarSrc="/veebee.png"
            agentName="KIBA"
            statusLabel={liveMode ? "CONNECTING LIVE AVATAR" : "PREPARING CHAT"}
            headline={<>KIBA<br /><span style={{ color: "var(--kl-red)" }}>IS JOINING</span><br />THE ROOM.</>}
            metaLine={`CHAT · ${lang === "hi" ? "हिन्दी" : lang === "bho" ? "भोजपुरी" : "ENGLISH"} · ${liveMode ? "LIVE AVATAR" : "AI VOICE"}`}
            progressDuration={3}
          />
        )}

        {/* ── CHAT ─────────────────────────────────────────────────────────── */}
        {stage === "chat" && (
          <ChatMode
            player={player}
            agents={agents}
            lang={lang}
            setLang={setLang}
            liveMode={liveMode}
            chatHistory={chatHistory}
            chatInput={chatInput}
            setChatInput={setChatInput}
            chatLoading={chatLoading}
            chatListening={chatListening}
            toggleChatMic={toggleChatMic}
            sendChat={sendChat}
            chatEndRef={chatEndRef}
            onBack={() => { setStage("mode"); setShowInstructions(true); }}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QuizQuestion component — manages speaking → answering → feedback flow
// ─────────────────────────────────────────────────────────────────────────────
function QuizQuestion({
  session, qIdx, answers, selected, setSelected,
  subStage, setSubStage, timer, lang, setLang,
  agentKey, agentInfo, agentGender,
  useLiveAvatar = false,
  voiceActive, startVoiceListen, stopVoiceListen,
  onSubmit, onAdvance, onBack, player,
}) {
  const q = session.questions[qIdx];
  const total = session.questions.length;

  // LiveAvatar state — only relevant when useLiveAvatar is true
  const liveAvatarRef = useRef(null);
  const [avatarConnected, setAvatarConnected] = useState(false);

  // Warmup window: give LiveAvatar up to 4 s to connect before falling back to ElevenLabs.
  const [isWarmingUp, setIsWarmingUp] = useState(useLiveAvatar);
  useEffect(() => {
    if (!useLiveAvatar) return;
    const id = setTimeout(() => setIsWarmingUp(false), 4000);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on first mount — intentional

  // When LiveAvatar status changes
  const handleAvatarStatusChange = useCallback((s) => {
    if (!useLiveAvatar) return;
    const connected = s === "connected";
    setAvatarConnected(connected);
    if (connected) {
      setIsWarmingUp(false); // end warmup early — live avatar is ready
      if (subStage === "speaking") {
        liveAvatarRef.current?.speak(q.question);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLiveAvatar, subStage, q?.question]);

  // Speak via LiveAvatar when question changes (if already connected)
  useEffect(() => {
    if (!useLiveAvatar || subStage !== "speaking" || !avatarConnected) return;
    liveAvatarRef.current?.speak(q.question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qIdx]);

  // When agent finishes speaking → reveal options
  const handleSpokeDone = useCallback(() => {
    if (subStage === "speaking") setSubStage("answering");
  }, [subStage, setSubStage]);

  // For feedback stage: auto-advance after 2s
  useEffect(() => {
    if (subStage !== "feedback") return;
    const id = setTimeout(() => onAdvance(), 2000);
    return () => clearTimeout(id);
  }, [subStage, onAdvance]);

  const submittedAnswer = answers[qIdx];
  const isCorrect = subStage === "feedback" && submittedAnswer && submittedAnswer === q.correct;
  const isFeedback = subStage === "feedback";

  // Determine avatar from (topic × gender × lang) combination
  const TOPIC_GROUP_MAP = { science: "A", general_knowledge: "A", know_your_bihar: "B", hindi_cinema: "B" };
  const COMBO_AVATAR = {
    "A-male-en": "/male-1.png", "A-male-hi": "/male-2.png", "A-male-bho": "/33-boy.png",
    "A-female-en": "/fe-1.png",  "A-female-hi": "/fe-3.png",  "A-female-bho": "/fe-4.png",
    "B-male-en": "/male-3.png",  "B-male-hi": "/male-4.png",  "B-male-bho": "/33-boy.png",
    "B-female-en": "/fe-6.svg",  "B-female-hi": "/fe-2.png",  "B-female-bho": "/fe-3.png",
  };
  const COMBO_NAME = {
    "A-male-en": "John",   "A-male-hi": "James",   "A-male-bho": "Lallan",
    "A-female-en": "Emma", "A-female-hi": "Nimmi",  "A-female-bho": "Gudiya",
    "B-male-en": "Peter",  "B-male-hi": "Russell",  "B-male-bho": "Bablu",
    "B-female-en": "Lilly","B-female-hi": "Bella",  "B-female-bho": "Champa",
  };
  const topicId = session.topic || "";
  const group = TOPIC_GROUP_MAP[topicId] || "A";
  const comboKey = `${group}-${agentGender || "female"}-${lang}`;
  const avatarSrc = COMBO_AVATAR[comboKey] || agentInfo.avatar_image_url || LOCAL_AVATAR_PATHS[agentKey] || "";
  const agentName = COMBO_NAME[comboKey] || agentInfo.name || agentKey;

  return (
    /* Identical fixed layout to ChatMode — avatar left 46%, content right */
    <div className="fixed inset-x-0 bottom-0 z-20 flex" style={{ top: 65, background: "var(--kl-navy-deep)" }}>

      {/* ── LEFT: full-height agent portrait ── */}
      <div
        className="hidden lg:flex flex-col flex-shrink-0"
        style={{ width: "46%", position: "relative", background: "#000" }}
      >
        {/* Speaking glow */}
        <div className="absolute inset-0 pointer-events-none transition-all duration-500" style={{
          boxShadow: subStage === "speaking"
            ? "inset 0 0 80px rgba(230,57,70,0.35), 0 0 60px rgba(230,57,70,0.25)"
            : "none",
          zIndex: 3,
        }} />

        {useLiveAvatar ? (
          <>
            {/* ── LIVE AVATAR PATH ── */}
            <LiveAvatarVideo
              ref={liveAvatarRef}
              fallbackSrc={avatarSrc}
              fallbackAlt={agentKey}
              gender={agentGender}
              lang={lang}
              topic={session.topic || ""}
              onSpeakEnd={avatarConnected ? handleSpokeDone : undefined}
              onStatusChange={handleAvatarStatusChange}
              className="absolute inset-0 w-full h-full object-cover object-top"
              style={{ zIndex: 1 }}
            />

            {/* Warmup overlay — shown for up to 4 s while LiveAvatar connects */}
            {isWarmingUp && !avatarConnected && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 5,
                background: "rgba(6,10,31,0.55)",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 12,
              }}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 5 }}>
                  {[0,1,2,3,4].map((i) => (
                    <div key={i} style={{
                      width: 5, borderRadius: 3, background: "var(--kl-red)",
                      animation: `sound-bar-qa 0.55s ease-in-out ${i * 0.1}s infinite alternate`,
                      minHeight: 5,
                    }} />
                  ))}
                </div>
                <div style={{
                  fontFamily: "monospace", fontSize: 10, letterSpacing: "0.2em",
                  textTransform: "uppercase", color: "var(--kl-red)", opacity: 0.85,
                }}>
                  AI judge warming up…
                </div>
              </div>
            )}

            {/* Fallback audio (ElevenLabs) — only after warmup ends and LiveAvatar isn't connected */}
            {!avatarConnected && !isWarmingUp && (
              <div className="hidden">
                <AgentAvatar
                  agent={agentKey}
                  topic={session.topic || ""}
                  language={lang}
                  gender={agentGender}
                  text={q.question}
                  avatarUrl={agentInfo.avatar_image_url || ""}
                  mode="audio"
                  onDone={handleSpokeDone}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {/* ── AI VOICE PATH — static image + immediate ElevenLabs audio ── */}
            <img
              src={avatarSrc}
              alt={agentName}
              className="absolute inset-0 w-full h-full object-cover object-top"
              style={{ zIndex: 1 }}
            />
            <div className="hidden">
              <AgentAvatar
                agent={agentKey}
                topic={session.topic || ""}
                language={lang}
                gender={agentGender}
                text={q.question}
                avatarUrl={agentInfo.avatar_image_url || ""}
                mode="audio"
                onDone={handleSpokeDone}
              />
            </div>
          </>
        )}

        {/* Sound bars while speaking */}
        {subStage === "speaking" && (
          <div style={{
            position: "absolute", bottom: 80, left: 0, right: 0, zIndex: 4,
            display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 5,
          }}>
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={{
                width: 5, borderRadius: 3, background: "#e63946",
                animation: `sound-bar-qa 0.55s ease-in-out ${i * 0.08}s infinite alternate`,
                minHeight: 6,
              }} />
            ))}
          </div>
        )}

        {/* Right-edge blend */}
        <div className="absolute inset-y-0 right-0 w-20 pointer-events-none" style={{
          background: "linear-gradient(to right, transparent, rgba(10,14,26,0.92))", zIndex: 2,
        }} />

        {/* Name + skip at bottom */}
        <div className="absolute bottom-0 inset-x-0 py-4 text-center flex-shrink-0"
          style={{ background: "linear-gradient(transparent, rgba(6,10,31,0.98))", zIndex: 4 }}>
          <span className="font-display text-3xl uppercase tracking-tight" style={{ color: "var(--kl-red)" }}>
            {agentName}
          </span>
          <span className="font-display text-3xl uppercase tracking-tight text-white"> · Proctorly AI</span>
          {subStage === "speaking" && (
            <div className="mt-2">
              <button
                onClick={() => { if (useLiveAvatar && avatarConnected) liveAvatarRef.current?.interrupt(); setSubStage("answering"); }}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full font-mono-kl text-[10px] uppercase tracking-widest transition-all"
                style={{ border: "1px solid rgba(230,57,70,0.6)", background: "rgba(0,0,0,0.5)", color: "var(--kl-red)" }}
              >
                <Volume2 className="w-3 h-3" /> Skip intro
              </button>
            </div>
          )}
        </div>

      </div>

      {/* ── RIGHT: question + options ── */}
      <div className="flex flex-col flex-1 min-w-0 px-8 md:px-12 py-8 overflow-y-auto"
        style={{ background: "rgba(10,14,26,0.97)" }}>

        {/* Header row */}
        <div className="mb-5 flex-shrink-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-1">
                BLITZ · {player?.name?.toUpperCase()} · Q {qIdx + 1}/{total}
              </div>
              <div className="font-display text-4xl md:text-5xl uppercase leading-tight text-white">
                Question <span style={{ color: "var(--kl-red)" }}>{qIdx + 1}.</span>
              </div>
              <div className="font-mono-kl text-xs uppercase tracking-widest text-white/40 mt-1">
                Topic · {session.topic_label}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 pt-1">
              <span
                className="px-3 py-1 rounded-full font-mono-kl text-[10px] uppercase tracking-widest"
                style={{ background: "rgba(230,57,70,0.15)", color: "var(--kl-red)", border: "1px solid rgba(230,57,70,0.4)" }}
              >
                {lang === "hi" ? "हिन्दी" : "English"}
              </span>
              <button
                onClick={onBack}
                className="btn-ghost-dark text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mt-4">
            <div
              className="h-full bg-[color:var(--kl-red)] transition-all duration-500"
              style={{ width: `${((qIdx + (isFeedback ? 1 : 0)) / total) * 100}%` }}
            />
          </div>
        </div>

        {/* Timer — right-aligned */}
        {subStage === "answering" && (
          <div className="flex justify-end mb-2 flex-shrink-0">
            <QuizTimer time={timer} />
          </div>
        )}

        {/* Question text */}
        <div className="font-display text-2xl md:text-3xl lg:text-4xl leading-tight text-white mb-6 flex-shrink-0">
          {q.question}
        </div>

        {/* Speaking indicator */}
        {subStage === "speaking" && (
          <div className="flex items-center gap-3 text-white/40 font-mono-kl text-xs uppercase tracking-widest mb-5 flex-shrink-0">
            <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{
                  width: 3, height: 14, background: "var(--kl-red)", borderRadius: 2,
                  animation: `sound-bar-qa 0.55s ease-in-out ${i * 0.12}s infinite alternate`,
                }} />
              ))}
            </div>
            Agent speaking — options will appear when done
          </div>
        )}

        {/* MCQ Options */}
        {(subStage === "answering" || subStage === "feedback") && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-shrink-0">
            {q.options.map((opt, i) => {
              const letter = OPTION_LETTERS[i];
              const isSelected = selected === letter || submittedAnswer === letter;
              const isCorrectOpt = letter === q.correct;
              let style = "border-white/15 bg-white/02 text-white hover:border-white/40";
              if (isFeedback) {
                if (isCorrectOpt) style = "border-green-400 bg-green-900/25 text-white";
                else if (isSelected && !isCorrectOpt) style = "border-red-400 bg-red-900/20 text-white/70";
                else style = "border-white/10 bg-transparent text-white/40";
              } else if (isSelected) {
                style = "border-[color:var(--kl-red)] bg-[rgba(230,57,70,0.12)] text-white";
              }
              return (
                <button
                  key={letter}
                  disabled={isFeedback}
                  onClick={() => !isFeedback && setSelected(letter)}
                  className={`flex items-start gap-3 p-5 rounded-md border transition-all text-left option-appear ${style}`}
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <span className="font-display text-xl leading-none w-7 flex-shrink-0" style={{ color: "var(--kl-red)" }}>
                    {letter}.
                  </span>
                  <span className="text-base leading-snug">{opt.replace(/^[A-D]\.\s*/i, "")}</span>
                  {isFeedback && isCorrectOpt && <CheckCircle className="w-4 h-4 text-green-400 ml-auto flex-shrink-0 mt-0.5" />}
                  {isFeedback && isSelected && !isCorrectOpt && <XCircle className="w-4 h-4 text-red-400 ml-auto flex-shrink-0 mt-0.5" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Feedback */}
        {isFeedback && (
          <div className={`mt-5 p-4 rounded-md border text-sm leading-relaxed slide-up flex-shrink-0 ${isCorrect ? "border-green-400/40 bg-green-900/20 text-green-200" : "border-red-400/40 bg-red-900/20 text-red-200"}`}>
            <strong>{isCorrect ? "Correct!" : `Correct answer: ${q.correct}.`}</strong>{" "}
            {q.explanation}
          </div>
        )}

        {/* Controls */}
        {subStage === "answering" && (
          <div className="flex flex-wrap gap-3 mt-6 flex-shrink-0">
            <button
              onClick={() => onSubmit(selected)}
              disabled={!selected}
              className="btn-submit disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirm Answer <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={voiceActive ? stopVoiceListen : startVoiceListen}
              className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-mono-kl uppercase transition ${voiceActive ? "bg-[color:var(--kl-red)] text-white border-[color:var(--kl-red)]" : "border-white/20 text-white/70 hover:border-white/40"}`}
            >
              {voiceActive ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              {voiceActive ? "Stop" : "Speak"}
            </button>
            <button onClick={() => onSubmit("")} className="btn-ghost-dark text-sm">Skip</button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes sound-bar-qa {
          from { height: 5px; }
          to   { height: 24px; }
        }
        @keyframes option-appear {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );

}

// ─────────────────────────────────────────────────────────────────────────────
// QuizTimer — circular timer for 30s per question
// ─────────────────────────────────────────────────────────────────────────────
function QuizTimer({ time }) {
  const pct = (time / 30) * 100;
  const stroke = time <= 5 ? "#E63946" : time <= 10 ? "#FFB400" : "#FFFFFF";
  return (
    <div className="flex items-center gap-2">
      <div className="font-mono-kl text-xs uppercase tracking-widest text-white/50">Time Left</div>
      <div className="relative w-12 h-12">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="44" stroke="rgba(255,255,255,0.08)" strokeWidth="9" fill="none" />
          <circle
            cx="50" cy="50" r="44"
            stroke={stroke} strokeWidth="9" fill="none" strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 44}`}
            strokeDashoffset={`${2 * Math.PI * 44 * (1 - pct / 100)}`}
            style={{ transition: "stroke-dashoffset 1s linear, stroke 0.4s" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-display text-xl tracking-tight text-white">
          {time}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Result
// ─────────────────────────────────────────────────────────────────────────────
function Result({ result, player, session, scoreReveal, onReplay, navigate }) {
  const badgeStyle = BADGE_STYLE[result.badge] || BADGE_STYLE.Beginner;
  const verifyAbs = `${window.location.origin}/verify/${result.certificate_id}`;
  const shareText = encodeURIComponent(
    `I scored ${result.score}/10 on ${session.topic_label} via Proctorly AI at the Kiba Labs AI Summit! Verify: ${verifyAbs}`
  );

  return (
    <div className="mt-4 relative z-10 slide-up">
      <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">
        <span className="kl-accent-line mr-3" />VERDICT IN
      </div>
      <h2 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] tracking-tight text-white">
        {result.score >= 9 ? "Outstanding!" : result.score >= 7 ? "Sharp." : result.score >= 5 ? "Solid." : "Keep going."}
      </h2>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        {/* Score panel */}
        <div className="rounded-md border border-white/10 p-8" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="font-mono-kl text-xs uppercase tracking-widest text-white/50">Your Score</div>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="font-display text-[10rem] leading-none score-pop text-[color:var(--kl-red)]">
              {scoreReveal}
            </span>
            <span className="font-display text-4xl text-white/30">/10</span>
          </div>
          <span className="inline-block font-display text-lg uppercase px-4 py-1.5 mt-3 rounded-full" style={badgeStyle}>
            {result.badge}
          </span>
          <p className="mt-5 text-lg leading-relaxed text-white/80">
            <strong className="text-white">NOVA says:</strong> {result.feedback}
          </p>
        </div>

        {/* Certificate */}
        <div className="rounded-md border border-[color:var(--kl-red)]/30 p-6" style={{ background: "rgba(230,57,70,0.04)" }}>
          <div className="font-mono-kl text-xs uppercase tracking-widest text-white/50 mb-3">Certificate</div>
          <Certificate result={result} player={player} session={session} />
        </div>
      </div>

      {/* ── Prize eligible banner ──────────────────────────────────────── */}
      {result.prize_eligible ? (
        <div className="mt-6 rounded-xl border-2 p-6 flex items-center gap-5"
          style={{ borderColor: "rgba(255,209,102,0.7)", background: "linear-gradient(135deg, rgba(255,209,102,0.12), rgba(230,57,70,0.08))", boxShadow: "0 0 30px rgba(255,209,102,0.15)" }}>
          <div className="text-5xl shrink-0">🎁</div>
          <div>
            <div className="font-display text-2xl uppercase text-[color:var(--kl-yellow)]">
              You're eligible for a prize!
            </div>
            <p className="text-white/75 text-sm mt-1.5">
              You scored <strong className="text-[color:var(--kl-yellow)]">{result.score}/10 ({result.certificate_percent}%)</strong> — that's 80%+!&nbsp;
              Head to the <strong className="text-white">Proctorly booth</strong> and show your score to claim your reward. 🏆
            </p>
          </div>
        </div>
      ) : result.score > 0 && (
        <div className="mt-6 rounded-xl border border-white/10 p-4 flex items-center gap-4"
          style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="text-3xl shrink-0">🎯</div>
          <p className="text-white/50 text-sm">
            Score <strong className="text-white">8/10 (80%) or higher</strong> to be eligible for a prize at the Proctorly booth.
            You got {result.score}/10 — keep practising and try again!
          </p>
        </div>
      )}

      {/* Answer breakdown */}
      {result.answers && result.answers.length > 0 && (
        <div className="mt-6 rounded-md border border-white/10 p-6" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="font-mono-kl text-xs uppercase tracking-widest text-[color:var(--kl-yellow)] mb-4">
            Answer Breakdown
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {result.answers.map((a, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                {a.is_correct
                  ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                <span className="text-white/60">Q{i + 1}</span>
                <span className={a.is_correct ? "text-green-300" : "text-red-300"}>
                  {a.selected || "—"} {!a.is_correct && `(correct: ${a.correct})`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-7">
        <button onClick={onReplay} className="btn-submit">
          Play again <RotateCcw className="w-4 h-4" />
        </button>
        <a href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer" className="btn-ghost-dark inline-flex items-center gap-2">
          Share on WhatsApp
        </a>
        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyAbs)}`} target="_blank" rel="noreferrer" className="btn-ghost-dark inline-flex items-center gap-2">
          Share on LinkedIn
        </a>
        <button onClick={() => navigate("/")} className="btn-ghost-dark">Back to home</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Certificate — "Proctorly AI" branding
// ─────────────────────────────────────────────────────────────────────────────
function Certificate({ result, player, session }) {
  const qr = `${process.env.REACT_APP_BACKEND_URL}/api/verify/${result.certificate_id}/qr.png`;
  const date = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="bg-white border border-black/10 p-6 rounded-md text-[color:var(--kl-ink)]">
      {/* Header */}
      <div className="flex items-start justify-between border-b-2 border-black/10 pb-3">
        <div>
          <div className="font-display text-2xl tracking-tight">
            PROCTORLY <span className="text-[color:var(--kl-red)]">AI</span>
          </div>
          <div className="font-mono-kl text-[9px] uppercase tracking-widest text-black/40 mt-0.5">
            Powered by Kiba Labs
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-kl text-[9px] uppercase tracking-widest text-black/50">Cert ID</div>
          <div className="font-mono-kl text-[10px] text-black/70">{result.certificate_id}</div>
          <div className="font-mono-kl text-[9px] text-black/40 mt-0.5">{date}</div>
        </div>
      </div>

      {/* Body */}
      <div className="mt-4">
        <div className="font-mono-kl text-[10px] uppercase tracking-widest text-black/45">
          This certifies that
        </div>
        <div className="font-display text-4xl md:text-5xl uppercase mt-1 leading-tight">
          {player.name}
        </div>
        <div className="font-mono-kl text-[10px] uppercase tracking-widest text-black/45 mt-3">
          has successfully completed the Proctorly AI Quiz on
        </div>
        <div className="font-display text-xl uppercase mt-0.5">{session.topic_label}</div>
        <div className="mt-3 flex items-center gap-4">
          <div>
            <div className="font-mono-kl text-[10px] uppercase tracking-widest text-black/45">Score</div>
            <div className="font-display text-5xl text-[color:var(--kl-red)] leading-none mt-0.5">
              {result.certificate_percent}%
            </div>
          </div>
          <div>
            <div className="font-mono-kl text-[10px] uppercase tracking-widest text-black/45">Level</div>
            <span className="inline-block bg-[color:var(--kl-red)] text-white px-3 py-1 mt-1 font-display uppercase rounded-full text-sm">
              {result.certificate_level}
            </span>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-black/50 leading-relaxed">
          This certificate validates participation and performance in an AI-assessed knowledge quiz,
          evaluated by Proctorly AI Engine (Claude Sonnet 4.6) at the Kiba Labs AI Summit.
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-end justify-between mt-4 border-t-2 border-black/10 pt-3">
        <div className="font-mono-kl text-[9px] uppercase text-black/50">
          Verified by Proctorly AI Engine · Claude Sonnet 4.6
        </div>
        <img src={qr} alt="verify QR" width={72} height={72} className="border border-black/20" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ChatMode — casual conversation with NOVA
// ─────────────────────────────────────────────────────────────────────────────
function ChatMode({
  player, agents, lang, setLang,
  liveMode = false,
  chatHistory, chatInput, setChatInput,
  chatLoading, chatListening, toggleChatMic,
  sendChat, chatEndRef, onBack,
}) {
  const agentKey  = "kiba";
  const agentInfo = agents[agentKey] || {};

  // ── Live Avatar state (only used when liveMode is true) ──
  const liveAvatarRef    = useRef(null);
  const [avatarConnected, setAvatarConnected] = useState(false);
  const [isWarmingUp, setIsWarmingUp]         = useState(liveMode);
  // chatRevealed: keep overlay until avatar is connected OR max 8s has passed
  const [chatRevealed, setChatRevealed]       = useState(!liveMode);
  const lastSpokenIdxRef = useRef(-1);

  // Hard timeout: reveal chat after 8 s even if avatar hasn't connected
  useEffect(() => {
    if (!liveMode) return;
    const id = setTimeout(() => {
      setIsWarmingUp(false);
      setChatRevealed(true);
    }, 8000);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on first mount — intentional

  // Speak each new KIBA response via Live Avatar
  useEffect(() => {
    if (!liveMode || !avatarConnected) return;
    if (chatHistory.length === 0) return;
    const lastMsg = chatHistory[chatHistory.length - 1];
    if (!lastMsg || lastMsg.role !== "assistant") return;
    const idx = chatHistory.length - 1;
    if (idx <= lastSpokenIdxRef.current) return;
    lastSpokenIdxRef.current = idx;
    liveAvatarRef.current?.speak(lastMsg.content);
  }, [chatHistory, avatarConnected, liveMode]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); }
  };

  return (
    /* Full viewport, sits below the sticky Header (z-30, ~64px tall) */
    <div className="fixed inset-x-0 bottom-0 z-20 flex" style={{ top: 65, background: "var(--kl-navy-deep)" }}>

      {/* ── LEFT: full-height Kiba portrait ─────────────────────────── */}
      <div
        className="hidden lg:flex flex-col flex-shrink-0"
        style={{ width: "46%", position: "relative", background: "#000" }}
      >
        {/* Speaking glow ring */}
        <div
          className="absolute inset-0 pointer-events-none transition-all duration-500"
          style={{
            boxShadow: chatLoading
              ? "inset 0 0 80px rgba(230,57,70,0.45), 0 0 60px rgba(230,57,70,0.35)"
              : "none",
            zIndex: 3,
          }}
        />

        {/* ── LIVE AVATAR PATH ── */}
        {liveMode ? (
          <>
            <LiveAvatarVideo
              ref={liveAvatarRef}
              fallbackSrc="/veebee.png"
              fallbackAlt="Kiba"
              gender="female"
              lang={lang}
              topic="kiba"
              onStatusChange={(s) => {
                const connected = s === "connected";
                setAvatarConnected(connected);
                if (connected) {
                  setIsWarmingUp(false);
                  setChatRevealed(true);
                }
              }}
              className="absolute inset-0 w-full h-full object-cover object-top"
              style={{ zIndex: 1 }}
            />
            {/* Warmup overlay — shown while connecting */}
            {isWarmingUp && !avatarConnected && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 5,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                background: "rgba(6,10,31,0.75)", backdropFilter: "blur(4px)",
              }}>
                <div style={{ fontFamily: "monospace", fontSize: 11, letterSpacing: "0.25em", color: "rgba(230,57,70,0.7)", marginBottom: 10 }}>
                  CONNECTING LIVE AVATAR…
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[0,1,2].map((i) => (
                    <div key={i} style={{
                      width: 8, height: 8, borderRadius: "50%", background: "var(--kl-red)",
                      animation: `pulse 1s ease-in-out ${i*0.15}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
            {/* Static fallback after warmup window expires without connection */}
            {!avatarConnected && !isWarmingUp && (
              <img
                src="/veebee.png"
                alt="Kiba"
                className="absolute inset-0 w-full h-full object-cover object-top"
                style={{ zIndex: 1 }}
              />
            )}
          </>
        ) : (
          <>
            {/* ── AI VOICE PATH — static image + ElevenLabs audio ── */}
            <img
              src="/veebee.png"
              alt="Kiba"
              className="absolute inset-0 w-full h-full object-cover object-top"
              style={{ zIndex: 1 }}
              onError={(e) => { e.target.src = LOCAL_AVATAR_PATHS[agentKey] || "/chankya.png"; }}
            />
            <div className="hidden">
              <AgentAvatar
                agent={agentKey}
                topic=""
                language={lang}
                text={chatHistory.length > 0 && chatHistory[chatHistory.length - 1]?.role === "assistant"
                  ? chatHistory[chatHistory.length - 1].content
                  : ""}
                avatarUrl={agentInfo.avatar_image_url || ""}
                mode="audio"
              />
            </div>
          </>
        )}

        {/* Sound bars — animated while speaking */}
        {chatLoading && (
          <div style={{
            position: "absolute", bottom: 80, left: 0, right: 0, zIndex: 4,
            display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 5,
          }}>
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={{
                width: 5, borderRadius: 3,
                background: "#e63946",
                animation: `sound-bar-qa 0.55s ease-in-out ${i * 0.08}s infinite alternate`,
                minHeight: 6,
              }} />
            ))}
          </div>
        )}

        {/* Right-edge gradient to blend into chat */}
        <div
          className="absolute inset-y-0 right-0 w-20 pointer-events-none"
          style={{ background: "linear-gradient(to right, transparent, rgba(10,14,26,0.92))", zIndex: 2 }}
        />

        {/* Name label at bottom */}
        <div
          className="absolute bottom-0 inset-x-0 py-4 text-center flex-shrink-0"
          style={{ background: "linear-gradient(transparent, rgba(6,10,31,0.98))", zIndex: 4 }}
        >
          <span className="font-display text-3xl uppercase tracking-tight text-[color:var(--kl-red)]">Kiba</span>
          <span className="font-display text-3xl uppercase tracking-tight text-white"> · Proctorly AI</span>
        </div>
      </div>

      {/* ── RIGHT: header + messages + input ─────────────────────────── */}
      <div
        className="flex flex-col flex-1 min-w-0 px-8 md:px-12 py-8"
        style={{ background: "rgba(10,14,26,0.97)" }}
      >
        {/* Header row */}
        <div className="mb-6 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display text-5xl md:text-6xl uppercase leading-tight text-white">
              Talk to <span className="text-[color:var(--kl-red)]">Kiba</span>
            </h1>
            <div className="flex items-center gap-2 flex-shrink-0 pt-2">
              {!liveMode && <LanguageToggle value={lang} onChange={setLang} />}
              {liveMode && (
                <span style={{
                  fontFamily: "monospace", fontSize: 10, letterSpacing: "0.22em",
                  textTransform: "uppercase", color: "rgba(230,57,70,0.8)",
                  border: "1px solid rgba(230,57,70,0.35)",
                  borderRadius: 6, padding: "4px 10px",
                }}>
                  🎥 LIVE · EN
                </span>
              )}
              <button onClick={onBack} className="btn-ghost-dark text-sm flex items-center gap-1.5 whitespace-nowrap">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </div>
          </div>
          <p className="text-white/50 text-sm mt-1 font-mono-kl uppercase tracking-widest">
            Career · AI · Technology · Future · Anything
          </p>
        </div>

          {/* Messages — scrollable */}
          <div
            className="flex-1 overflow-y-auto pr-2 space-y-4 mb-5"
          >
            {chatHistory.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`w-full px-5 py-4 rounded-2xl text-base md:text-lg leading-relaxed ${
                    msg.role === "user"
                      ? "bg-[color:var(--kl-red)] text-white rounded-br-sm"
                      : "border border-white/15 text-white/90 rounded-bl-sm"
                  }`}
                  style={msg.role === "assistant" ? { background: "rgba(255,255,255,0.06)" } : {}}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div
                  className="px-4 py-3 rounded-2xl rounded-bl-sm border border-white/15 flex gap-1.5 items-center"
                  style={{ background: "rgba(255,255,255,0.06)" }}
                >
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="w-2 h-2 rounded-full bg-[color:var(--kl-red)]" style={{ animation: `pulse 1s ease-in-out ${i * 0.15}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input row */}
          <div className="flex gap-3 items-end flex-shrink-0">
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type or speak your message… (Enter to send)"
              rows={2}
              className="input-dark flex-1 resize-none text-base"
              style={{ minHeight: 68 }}
            />
            <button
              onClick={toggleChatMic}
              className={`p-3 rounded-md border transition ${chatListening ? "bg-[color:var(--kl-red)] text-white border-[color:var(--kl-red)]" : "border-white/20 text-white/60 hover:border-white/40 hover:text-white"}`}
            >
              {chatListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <button
              onClick={() => sendChat()}
              disabled={!chatInput.trim() && !chatLoading}
              className="btn-submit px-4 py-3 disabled:opacity-40"
            >
              <ArrowUpRight className="w-5 h-5" />
            </button>
          </div>
      </div>

      {/* ── Live Avatar connecting overlay — shown until avatar is ready ── */}
      {liveMode && !chatRevealed && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 40,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "var(--kl-navy-deep)",
        }}>
          {/* Animated keyframes (scoped) */}
          <style>{`
            @keyframes kiba-connect-float {
              0%,100% { transform: translateY(0px) scale(1); }
              50% { transform: translateY(-12px) scale(1.02); }
            }
            @keyframes kiba-connect-ring1 {
              0%,100% { transform: scale(1); opacity: 0.55; }
              50% { transform: scale(1.2); opacity: 0.08; }
            }
            @keyframes kiba-connect-ring2 {
              0%,100% { transform: scale(1); opacity: 0.3; }
              50% { transform: scale(1.4); opacity: 0.04; }
            }
            @keyframes kiba-connect-scan {
              0% { top: -3px; opacity: 0.8; }
              85% { opacity: 0.8; }
              100% { top: 100%; opacity: 0; }
            }
            @keyframes kiba-connect-glow {
              0%,100% { text-shadow: 0 0 18px rgba(230,57,70,0.45); }
              50% { text-shadow: 0 0 45px rgba(230,57,70,1), 0 0 70px rgba(230,57,70,0.4); }
            }
            @keyframes kiba-connect-bar {
              0% { width: 0%; }
              70% { width: 80%; }
              100% { width: 95%; }
            }
            @keyframes kiba-connect-dot {
              0%,100% { opacity: 1; }
              50% { opacity: 0.15; }
            }
          `}</style>

          <div style={{ display: "flex", gap: 56, alignItems: "center", justifyContent: "center" }}>

            {/* Portrait */}
            <div style={{ position: "relative", flexShrink: 0, width: 260, height: 340 }}>
              <div style={{
                position: "absolute", inset: "-28px", borderRadius: "50%",
                border: "2px solid rgba(230,57,70,0.5)",
                animation: "kiba-connect-ring1 2.4s ease-in-out infinite",
              }} />
              <div style={{
                position: "absolute", inset: "-52px", borderRadius: "50%",
                border: "1px solid rgba(230,57,70,0.25)",
                animation: "kiba-connect-ring2 2.4s ease-in-out 0.5s infinite",
              }} />
              <div style={{
                position: "absolute", inset: 0,
                background: "radial-gradient(ellipse at 50% 80%, rgba(230,57,70,0.4) 0%, transparent 65%)",
                borderRadius: 20, filter: "blur(18px)",
              }} />
              <div style={{
                position: "relative", width: "100%", height: "100%",
                borderRadius: 18, overflow: "hidden",
                border: "2px solid rgba(230,57,70,0.55)",
                boxShadow: "0 0 35px rgba(230,57,70,0.35), 0 0 70px rgba(230,57,70,0.12)",
                animation: "kiba-connect-float 3.5s ease-in-out infinite",
                background: "#000",
              }}>
                <img src="/veebee.png" alt="Kiba" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                <div style={{
                  position: "absolute", left: 0, right: 0, height: 3,
                  background: "linear-gradient(90deg, transparent, rgba(230,57,70,0.9), transparent)",
                  animation: "kiba-connect-scan 2.2s linear infinite",
                  pointerEvents: "none",
                }} />
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0, height: "40%",
                  background: "linear-gradient(to top, rgba(6,10,31,0.95), transparent)",
                }} />
                <div style={{
                  position: "absolute", bottom: 14, left: 0, right: 0, textAlign: "center",
                  fontFamily: "var(--font-display, sans-serif)", fontSize: 22, fontWeight: 900,
                  letterSpacing: "0.1em", textTransform: "uppercase", color: "#fff",
                  animation: "kiba-connect-glow 2s ease-in-out infinite",
                }}>KIBA</div>
              </div>
            </div>

            {/* Status text */}
            <div style={{ maxWidth: 320 }}>
              <div style={{
                fontFamily: "monospace", fontSize: 10, letterSpacing: "0.3em",
                textTransform: "uppercase", color: "rgba(230,57,70,0.7)", marginBottom: 14,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: "50%", background: "var(--kl-red)",
                  animation: "kiba-connect-dot 1s ease-in-out infinite",
                }} />
                CONNECTING LIVE AVATAR
              </div>

              <div style={{
                fontFamily: "var(--font-display, sans-serif)",
                fontSize: 42, fontWeight: 900, lineHeight: 0.95,
                textTransform: "uppercase", color: "#fff",
                letterSpacing: "-0.01em",
              }}>
                KIBA<br />
                <span style={{ color: "var(--kl-red)" }}>is joining</span><br />
                the room.
              </div>

              <div style={{
                marginTop: 24, fontFamily: "monospace", fontSize: 11,
                color: "rgba(255,255,255,0.35)", letterSpacing: "0.12em",
              }}>
                LIVE AVATAR · {lang === "hi" ? "हिन्दी" : lang === "bho" ? "भोजपुरी" : "ENGLISH"}
              </div>

              {/* Progress bar — animates toward 95% and waits */}
              <div style={{
                marginTop: 18, height: 2, borderRadius: 2,
                background: "rgba(255,255,255,0.07)", overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", borderRadius: 2,
                  background: "linear-gradient(90deg, var(--kl-red), #ff6b6b)",
                  animation: "kiba-connect-bar 8s ease-out forwards",
                  boxShadow: "0 0 10px rgba(230,57,70,0.75)",
                }} />
              </div>

              {/* Pulsing dots */}
              <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
                {[0,1,2].map((i) => (
                  <div key={i} style={{
                    width: 8, height: 8, borderRadius: "50%", background: "var(--kl-red)",
                    animation: `pulse 1s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
                <span style={{
                  fontFamily: "monospace", fontSize: 10, letterSpacing: "0.2em",
                  color: "rgba(230,57,70,0.55)", marginLeft: 8, alignSelf: "center",
                  textTransform: "uppercase",
                }}>Please wait…</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
